/**
 * loadGLB — the Load stage for glTF-binary (ADR-008 §2). Format knowledge
 * ends here: downstream conversion consumes the loaded scene graph only.
 * Conventions are DECLARED from the glTF spec (Y-up, meters, +Z facing) with
 * provenance — never sniffed. Surfaces the authored-name lookup (§2j) and
 * the unhandled-extension inventory (§2k) that conversion reports need.
 * Depends on: three (GLTFLoader addon). Depended on by: app/import, fixtures.
 */
import { Object3D, type Material } from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { AnimationClip } from 'three';
import type { ConventionDeclaration } from './import-report.js';
import { recordFilePaint } from './zones/file-paint.js';
import {
  fromVrmcSpringBone,
  isSoftTissueSpring,
  softTissueExtrasSchema,
  VRMC_SPRING_BONE,
  type SoftTissueExtras,
  type SpringDeclaration,
} from '@harryhausen/kernel';
import { rememberDeclaredSprings } from './springs/declared-springs.js';
import { rememberDeclaredSoftTissue } from './springs/declared-soft-tissue.js';

/** glTF 2.0 spec conventions — fixed by the format, cited as such. */
export const GLTF_SPEC_CONVENTION: ConventionDeclaration = Object.freeze({
  metersPerUnit: 1,
  upAxis: 'Y',
  facing: '+Z',
  provenance: 'spec:gltf',
});

/**
 * VRM 0.x conventions — the SAME container, a DIFFERENT governing spec for
 * facing: VRM 0.x authors models facing −Z (the 180° flip three-vrm applies
 * at load; VRM 1.0 moved to glTF's +Z, which is why the extension NAME is
 * the discriminator — `VRM` is 0.x, `VRMC_vrm` is 1.0). Declared, not
 * sniffed: the extension's presence IS the declaration. MEASURED 2026-08-03
 * on the VRoid corpus (Anon class): toes behind ankles in Z, eyes behind
 * head, anatomical left on −X — three independent witnesses, all −Z, while
 * the VRM1 files measure +Z. Declaring +Z here inverted every facing-derived
 * sign on the family (arm-space "wider makes narrower" was the field
 * symptom, 2026-08-03).
 */
export const VRM0_SPEC_CONVENTION: ConventionDeclaration = Object.freeze({
  metersPerUnit: 1,
  upAxis: 'Y',
  facing: '-Z',
  provenance: 'spec:vrm0',
});

export interface LoadedGlb {
  readonly scene: Object3D;
  readonly clips: readonly AnimationClip[];
  readonly convention: ConventionDeclaration;
  /** Authored (file) node name lookup — GLTFLoader sanitizes runtime names (ADR-008 §2j). */
  readonly authoredNameFor: (object: Object3D) => string | undefined;
  /** asset.generator platform provenance, if the file carries one. */
  readonly generator: string | null;
  /** extensionsUsed entries no loader plugin handled — enumerated, never silent (ADR-008 §2k). */
  readonly unhandledExtensions: readonly string[];
  /**
   * Names this loader MINTED for materials the file left unnamed — reported,
   * never silent, because it writes into the asset. Empty for every specimen
   * in the corpus: Tripo names all of them.
   */
  readonly mintedMaterials: readonly string[];
  /**
   * ADR-038: the spring chains the file declares (`VRMC_springBone` 1.0), by
   * the file's node names — empty when it declares none. Also remembered
   * against `scene` ({@link declaredSprings}) for doors that only hold the scene.
   */
  readonly springs: readonly SpringDeclaration[];
}

interface GltfJson {
  readonly asset?: { readonly generator?: string };
  readonly materials?: readonly { readonly name?: string }[];
  readonly extensionsUsed?: readonly string[];
  readonly nodes?: ReadonlyArray<{ readonly name?: string }>;
  readonly extensions?: Readonly<Record<string, unknown>>;
}

export async function loadGLB(bytes: ArrayBuffer, loader: GLTFLoader = new GLTFLoader()): Promise<LoadedGlb> {
  const gltf: GLTF = await loader.parseAsync(bytes, '');
  const parser = gltf.parser as {
    json: GltfJson;
    associations: Map<Object3D | Material, { nodes?: number; materials?: number } | undefined>;
    extensions?: Record<string, unknown>;
    plugins?: Record<string, unknown>;
  };
  const json = parser.json;

  const authoredNameFor = (object: Object3D): string | undefined => {
    const nodeIndex = parser.associations.get(object)?.nodes;
    if (nodeIndex === undefined) return undefined;
    return json.nodes?.[nodeIndex]?.name;
  };

  // ---- identity for materials the file left unnamed ----
  //
  // A material's NAME is its identity everywhere downstream: the Materials
  // panel's row, the Document's per-material edit, and the match the texture
  // derive makes on the way out. A file that names nothing therefore forced a
  // synthetic identity invented at display time — positional, unstable across
  // a re-import, and the source of a P1 in three consecutive review rounds
  // (colliding with real names, merging distinct materials into one row,
  // shipping a detachment the export could not address).
  //
  // So identity is MINTED HERE and written into the asset, exactly as Tripo
  // already does (`tripo_mat_<uuid>` on all 37 corpus specimens — measured
  // 2026-08-27: not one unnamed material among them). Once named, the name
  // survives export, re-import, and every other tool.
  //
  // DETERMINISTIC, from the glTF material index, not random: a UUID minted per
  // import is not stable until the first export, so a Document saved before
  // exporting would key on an id that a fresh import of the same file never
  // reproduces. Collisions with an authored name are stepped over.
  const authored = new Set((json.materials ?? []).map((m) => m.name).filter((n): n is string => typeof n === 'string' && n !== ''));
  const mintedMaterials: string[] = [];
  gltf.scene.traverse((node) => {
    const mesh = node as { material?: Material | Material[] };
    if (mesh.material === undefined) return;
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      if (material.name !== '') continue;
      const index = parser.associations.get(material)?.materials;
      let minted = index === undefined ? `material_${mintedMaterials.length}` : `material_${index}`;
      let bump = 0;
      while (authored.has(minted)) {
        bump += 1;
        minted = index === undefined ? `material_${mintedMaterials.length}_${bump}` : `material_${index}_${bump}`;
      }
      authored.add(minted);
      material.name = minted;
      mintedMaterials.push(minted);
    }
  });

  // ADR-038: the spring declaration is read HERE (a root extension never
  // reaches the scene graph), refused by name when it points at nodes the file
  // does not have — a broken declaration is never a silently shorter chain.
  const springExt = json.extensions?.[VRMC_SPRING_BONE];
  const springs = springExt === undefined ? [] : fromVrmcSpringBone(springExt, (i) => json.nodes?.[i]?.name);
  // ADR-038 J2: soft-tissue springs (`jiggle-*`) go to their own table with the
  // region surface their extras carry; every other spring stays the tail's.
  // A malformed surface is read as absent (the spring itself still plays) —
  // the numbers a VRM reader uses are the joints', not the extras'.
  const rawSprings = (springExt as { springs?: { extras?: { poqpoq?: { jiggle?: unknown } } }[] } | undefined)?.springs ?? [];
  const softTissue = springs.flatMap((decl, i) => {
    if (!isSoftTissueSpring(decl)) return [];
    const parsed = softTissueExtrasSchema.safeParse(rawSprings[i]?.extras?.poqpoq?.jiggle);
    return [{ decl, extras: parsed.success ? (parsed.data as SoftTissueExtras) : null }];
  });
  rememberDeclaredSprings(gltf.scene, springs.filter((d) => !isSoftTissueSpring(d)));
  rememberDeclaredSoftTissue(gltf.scene, softTissue);

  const handled = new Set([
    ...Object.keys(parser.extensions ?? {}),
    ...Object.keys(parser.plugins ?? {}),
    ...(springExt !== undefined ? [VRMC_SPRING_BONE] : []),
  ]);
  const unhandledExtensions = (json.extensionsUsed ?? []).filter((name) => !handled.has(name));

  // The skin as the file painted it, before any run re-paints it (zones/file-paint).
  recordFilePaint(gltf.scene);
  return {
    scene: gltf.scene,
    clips: gltf.animations,
    convention: (json.extensionsUsed ?? []).includes('VRM') ? VRM0_SPEC_CONVENTION : GLTF_SPEC_CONVENTION,
    authoredNameFor,
    generator: json.asset?.generator ?? null,
    mintedMaterials,
    unhandledExtensions,
    springs,
  };
}
